import {
	RiCheckLine,
	RiDeleteBinLine,
	RiDraftLine,
	RiEarthLine,
	RiHistoryLine,
	RiLoader4Line,
	RiTimeLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { AppBreadcrumb, type AppBreadcrumbItem } from "@/components/app-breadcrumb";
import { WikiEditor } from "@/components/editor/wiki-editor";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { usePermissions } from "@/hooks/use-permissions";
import { relativeTime } from "@/lib/utils";
import { useWikiTree, type WikiPageNode } from "@/routes/_app/wiki";

export const Route = createFileRoute("/_app/wiki/$pageId")({
	component: WikiDetailPage,
});

// --- Breadcrumb builder ---

function buildBreadcrumbs(pageId: string, pages: WikiPageNode[]): AppBreadcrumbItem[] {
	const pageMap = new Map(pages.map((p) => [p.id, p]));
	const crumbs: AppBreadcrumbItem[] = [{ label: "Wiki", href: "/wiki" }];
	const chain: WikiPageNode[] = [];

	let current = pageMap.get(pageId);
	while (current) {
		chain.unshift(current);
		current = current.parentId ? pageMap.get(current.parentId) : undefined;
	}

	for (const [i, p] of chain.entries()) {
		const isLast = i === chain.length - 1;
		crumbs.push({
			label: p.title,
			href: isLast ? undefined : `/wiki/${p.id}`,
			icon: p.icon ? <span className="text-sm">{p.icon}</span> : undefined,
		});
	}

	return crumbs;
}

// --- Save indicator states ---

type SaveStatus = "idle" | "saving" | "saved" | "error";

// --- Component ---

function WikiDetailPage() {
	const { pageId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { pages, refetchTree } = useWikiTree();
	const { can } = usePermissions();

	const canEdit = can("wiki", "edit");
	const canDelete = can("wiki", "delete");

	// Edit mode
	const [editing, setEditing] = useState(false);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const [editingTitle, setEditingTitle] = useState(false);
	const [titleValue, setTitleValue] = useState("");

	// Dialogs
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);

	// Pending save ref (for flushing on unmount/navigation)
	const pendingSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingContentRef = useRef<unknown>(null);

	// Fetch page detail
	const { data: page, isLoading } = $api.useQuery("get", "/api/v1/wiki/{id}", {
		params: { path: { id: pageId } },
	});

	// Fetch versions (only when history sheet is open)
	const { data: versions } = $api.useQuery(
		"get",
		"/api/v1/wiki/{id}/versions",
		{ params: { path: { id: pageId }, query: { limit: 20 } } },
		{ enabled: historyOpen },
	);

	const versionList = (versions ?? []) as Array<{
		id: string;
		versionNumber: number;
		title: string;
		editedByName?: string | null;
		createdAt: string;
	}>;

	// Reset edit mode when page changes
	useEffect(() => {
		setEditing(false);
		setSaveStatus("idle");
		setEditingTitle(false);
		// Flush any pending save
		if (pendingSaveRef.current) {
			clearTimeout(pendingSaveRef.current);
			pendingSaveRef.current = null;
		}
	}, []);

	// Init title value when page loads
	useEffect(() => {
		if (page) setTitleValue(page.title);
	}, [page]);

	// Keyboard shortcuts
	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			// Cmd+E to toggle edit mode
			if (canEdit && (e.metaKey || e.ctrlKey) && e.key === "e") {
				e.preventDefault();
				setEditing((prev) => !prev);
			}
			// Escape to exit edit mode
			if (e.key === "Escape" && editing) {
				setEditing(false);
			}
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [editing, canEdit]);

	// --- Save helpers ---

	const saveContent = useCallback(
		async (content: unknown) => {
			setSaveStatus("saving");
			try {
				const { error } = await fetchClient.PUT("/api/v1/wiki/{id}", {
					params: { path: { id: pageId } },
					body: { content },
				});
				if (error) {
					setSaveStatus("error");
					return;
				}
				setSaveStatus("saved");
				queryClient.invalidateQueries({
					queryKey: ["get", "/api/v1/wiki/{id}", { params: { path: { id: pageId } } }],
				});
			} catch {
				setSaveStatus("error");
			}
		},
		[pageId, queryClient],
	);

	const handleContentChange = useCallback(
		(content: unknown) => {
			pendingContentRef.current = content;
			// Debounce is already in WikiEditor (2s), so just save directly
			saveContent(content);
		},
		[saveContent],
	);

	async function handleTitleBlur() {
		setEditingTitle(false);
		if (!page || titleValue === page.title) return;
		if (!titleValue.trim()) {
			setTitleValue(page.title);
			return;
		}

		const { error } = await fetchClient.PUT("/api/v1/wiki/{id}", {
			params: { path: { id: pageId } },
			body: { title: titleValue },
		});
		if (error) {
			toast.error("Failed to update title");
			setTitleValue(page.title);
			return;
		}
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/wiki/{id}", { params: { path: { id: pageId } } }],
		});
		refetchTree();
	}

	async function handleToggleStatus() {
		if (!page) return;
		const newStatus = page.status === "draft" ? "published" : "draft";
		const { error } = await fetchClient.PUT("/api/v1/wiki/{id}", {
			params: { path: { id: pageId } },
			body: { status: newStatus },
		});
		if (error) {
			toast.error("Failed to update status");
			return;
		}
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/wiki/{id}", { params: { path: { id: pageId } } }],
		});
		refetchTree();
		toast.success(newStatus === "published" ? "Published" : "Moved to draft");
	}

	async function handleDelete() {
		setDeleting(true);
		try {
			const { error } = await fetchClient.DELETE("/api/v1/wiki/{id}", {
				params: { path: { id: pageId } },
			});
			if (error) {
				toast.error("Failed to delete page");
				return;
			}
			refetchTree();
			toast.success("Page deleted");
			await navigate({ to: "/wiki" });
		} finally {
			setDeleting(false);
			setDeleteOpen(false);
		}
	}

	async function handleRestore(versionId: string) {
		try {
			const { error } = await fetchClient.POST("/api/v1/wiki/{id}/versions/{version_id}/restore", {
				params: { path: { id: pageId, version_id: versionId } },
			});
			if (error) {
				toast.error("Failed to restore version");
				return;
			}
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/v1/wiki/{id}", { params: { path: { id: pageId } } }],
			});
			refetchTree();
			toast.success("Version restored");
		} catch {
			toast.error("Network error");
		}
	}

	// --- Loading state ---

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-5 w-48" />
				<Skeleton className="h-10 w-64" />
				<Skeleton className="h-4 w-40" />
				<Skeleton className="h-64 w-full mt-4" />
			</div>
		);
	}

	if (!page) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
				<p>Page not found</p>
				<Button variant="outline" size="sm" onClick={() => navigate({ to: "/wiki" })}>
					Back to Wiki
				</Button>
			</div>
		);
	}

	const breadcrumbs = buildBreadcrumbs(pageId, pages);
	const hasRichContent = Array.isArray(page.content) && page.content.length > 0;

	return (
		<div className="max-w-4xl">
			{/* Breadcrumb */}
			<AppBreadcrumb items={breadcrumbs} className="mb-4" />

			{/* Header */}
			<div className="flex items-start justify-between gap-4 mb-1">
				<div className="flex-1 min-w-0">
					{/* Editable title */}
					{editingTitle ? (
						<input
							className="w-full bg-transparent text-2xl font-bold tracking-tight outline-none border-b border-primary/30 pb-0.5"
							value={titleValue}
							onChange={(e) => setTitleValue(e.target.value)}
							onBlur={handleTitleBlur}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									(e.target as HTMLInputElement).blur();
								}
								if (e.key === "Escape") {
									setTitleValue(page.title);
									setEditingTitle(false);
								}
							}}
						/>
					) : (
						<h1
							className={`text-2xl font-bold tracking-tight rounded px-1 -mx-1 transition-colors ${canEdit ? "cursor-text hover:bg-muted/50" : ""}`}
							onClick={() => {
								if (!canEdit) return;
								setEditingTitle(true);
								setTitleValue(page.title);
							}}
						>
							{page.icon ? `${page.icon} ` : ""}
							{page.title}
						</h1>
					)}

					{/* Metadata line */}
					<div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
						{page.lastEditedByName && <span>Last edited by {page.lastEditedByName}</span>}
						<span>{relativeTime(page.updatedAt)}</span>

						{/* Save indicator */}
						{saveStatus === "saving" && (
							<span className="flex items-center gap-1 text-muted-foreground">
								<RiLoader4Line className="size-3 animate-spin" />
								Saving...
							</span>
						)}
						{saveStatus === "saved" && (
							<span className="flex items-center gap-1 text-green-600">
								<RiCheckLine className="size-3" />
								Saved
							</span>
						)}
						{saveStatus === "error" && <span className="text-destructive">Save failed</span>}
					</div>
				</div>

				{/* Actions */}
				<div className="flex items-center gap-1.5 shrink-0">
					{canEdit && (
						<Button
							variant="ghost"
							size="sm"
							onClick={handleToggleStatus}
							title={page.status === "draft" ? "Publish" : "Move to draft"}
						>
							{page.status === "draft" ? (
								<>
									<RiEarthLine className="mr-1.5 size-3.5" />
									Publish
								</>
							) : (
								<>
									<RiDraftLine className="mr-1.5 size-3.5" />
									Draft
								</>
							)}
						</Button>
					)}
					<Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
						<RiHistoryLine className="mr-1.5 size-3.5" />
						History
					</Button>
					{canDelete && (
						<Button
							variant="ghost"
							size="sm"
							className="text-destructive hover:text-destructive"
							onClick={() => setDeleteOpen(true)}
						>
							<RiDeleteBinLine className="size-3.5" />
						</Button>
					)}
				</div>
			</div>

			{/* Editor */}
			<div className="mt-6 min-h-[400px]" onClick={() => canEdit && !editing && setEditing(true)}>
				<WikiEditor
					key={pageId}
					initialContent={hasRichContent ? page.content : undefined}
					readOnly={!editing}
					onChange={handleContentChange}
				/>
				{canEdit && !editing && !hasRichContent && !page.plainText && (
					<p className="text-sm text-muted-foreground mt-4">Click here to start writing...</p>
				)}
			</div>

			{/* Delete confirmation */}
			<Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete page?</DialogTitle>
						<DialogDescription>
							This will permanently delete &ldquo;{page.title}&rdquo;. This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDeleteOpen(false)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete} disabled={deleting}>
							{deleting ? "Deleting..." : "Delete"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Version history sheet */}
			<Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
				<SheetContent side="right" className="sm:max-w-md">
					<SheetHeader>
						<SheetTitle>Version History</SheetTitle>
					</SheetHeader>
					<div className="flex-1 overflow-y-auto p-6 pt-0">
						{versionList.length === 0 ? (
							<p className="text-xs text-muted-foreground py-4">No versions yet.</p>
						) : (
							<div className="space-y-2">
								{versionList.map((v) => (
									<div
										key={v.id}
										className="flex items-center justify-between rounded-md border p-3 text-xs"
									>
										<div>
											<span className="font-mono text-muted-foreground">v{v.versionNumber}</span>{" "}
											<span className="font-medium">{v.title}</span>
											{v.editedByName && (
												<span className="text-muted-foreground"> by {v.editedByName}</span>
											)}
											<div className="flex items-center gap-1 mt-0.5 text-muted-foreground">
												<RiTimeLine className="size-3" />
												{relativeTime(v.createdAt)}
											</div>
										</div>
										{canEdit && (
											<Button variant="outline" size="sm" onClick={() => handleRestore(v.id)}>
												Restore
											</Button>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
}
